package fr.manyverse.app.presentation.components.modules.digicode

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Slider
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import fr.manyverse.app.domain.model.TableMember
import fr.manyverse.app.domain.validation.DigicodeValidator
import fr.manyverse.app.presentation.components.SwitchRow
import fr.manyverse.app.presentation.components.clearFocusOnTap
import fr.manyverse.app.presentation.components.modules.common.ParticipantSelectionMode
import fr.manyverse.app.presentation.components.modules.common.ParticipantSelector
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.cancel
import manyverse.shared.generated.resources.digicode_attempts_label
import manyverse.shared.generated.resources.digicode_launch
import manyverse.shared.generated.resources.digicode_launch_title
import manyverse.shared.generated.resources.digicode_show_attempts
import manyverse.shared.generated.resources.digicode_solution_label
import manyverse.shared.generated.resources.digicode_sound_enabled
import manyverse.shared.generated.resources.digicode_time_limit_enabled
import manyverse.shared.generated.resources.digicode_time_limit_label
import org.jetbrains.compose.resources.stringResource

/**
 * Dialog for the GM to configure and launch a digicode challenge.
 */
@Composable
fun DigicodeCreationDialog(
    showDialog: Boolean,
    players: List<TableMember>,
    labelOf: (TableMember) -> String = { it.userId.take(8) },
    isLoading: Boolean,
    errorMessage: String?,
    onDismiss: () -> Unit,
    onConfirm: (
        targetParticipantId: String,
        digitCount: Int,
        maxAttempts: Int,
        timeLimitSeconds: Int?,
        showAttemptsToPlayer: Boolean,
        soundEnabled: Boolean,
        solution: String
    ) -> Unit,
    modifier: Modifier = Modifier
) {
    var selectedPlayerId by remember(showDialog) { mutableStateOf("") }
    var solution by remember(showDialog) { mutableStateOf("") }
    var maxAttempts by remember(showDialog) { mutableFloatStateOf(3f) }
    var timeLimitEnabled by remember(showDialog) { mutableStateOf(false) }
    var timeLimitSlider by remember(showDialog) { mutableFloatStateOf(60f) }
    var showAttemptsToPlayer by remember(showDialog) { mutableStateOf(false) }
    var soundEnabled by remember(showDialog) { mutableStateOf(true) }

    val digitCountInt = solution.length
    val maxAttemptsInt = maxAttempts.toInt()
    val timeLimitSeconds = if (timeLimitEnabled) (timeLimitSlider / 30).toInt() * 30 else null

    val solutionValid = solution.length in DigicodeValidator.MIN_DIGIT_COUNT..DigicodeValidator.MAX_DIGIT_COUNT
    val canSubmit = selectedPlayerId.isNotBlank() && solutionValid && !isLoading

    if (!showDialog) return

    AlertDialog(
        properties = DialogProperties(dismissOnBackPress = false, dismissOnClickOutside = false),
        onDismissRequest = { if (!isLoading) onDismiss() },
        title = { Text(stringResource(Res.string.digicode_launch_title)) },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                // Target player selection (single)
                ParticipantSelector(
                    players = players,
                    mode = ParticipantSelectionMode.Single(
                        selectedId = selectedPlayerId,
                        onSelect = { selectedPlayerId = it }
                    ),
                    labelOf = labelOf,
                    isEnabled = !isLoading
                )

                // Solution input
                OutlinedTextField(
                    value = solution,
                    onValueChange = { newValue ->
                        solution = newValue.filter { it.isDigit() }.take(DigicodeValidator.MAX_DIGIT_COUNT)
                    },
                    label = { Text(stringResource(Res.string.digicode_solution_label)) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    singleLine = true,
                    isError = solution.isNotBlank() && solution.length < DigicodeValidator.MIN_DIGIT_COUNT,
                    enabled = !isLoading,
                    modifier = Modifier.fillMaxWidth()
                )

                // Max attempts slider
                Column {
                    Text(
                        text = stringResource(Res.string.digicode_attempts_label, maxAttemptsInt),
                        style = MaterialTheme.typography.labelMedium
                    )
                    Slider(
                        value = maxAttempts,
                        onValueChange = { maxAttempts = it },
                        valueRange = DigicodeValidator.MIN_ATTEMPTS.toFloat()..DigicodeValidator.MAX_ATTEMPTS.toFloat(),
                        steps = DigicodeValidator.MAX_ATTEMPTS - DigicodeValidator.MIN_ATTEMPTS - 1,
                        enabled = !isLoading
                    )
                }

                // Time limit toggle + slider
                SwitchRow(
                    label = stringResource(Res.string.digicode_time_limit_enabled),
                    checked = timeLimitEnabled,
                    onCheckedChange = { timeLimitEnabled = it },
                    enabled = !isLoading
                )
                if (timeLimitEnabled) {
                    val timeLimitInt = (timeLimitSlider / 30).toInt() * 30
                    Column {
                        Text(
                            text = stringResource(Res.string.digicode_time_limit_label, timeLimitInt),
                            style = MaterialTheme.typography.labelMedium
                        )
                        Slider(
                            value = timeLimitSlider,
                            onValueChange = { timeLimitSlider = it },
                            valueRange = DigicodeValidator.MIN_TIME_LIMIT_SECONDS.toFloat()..DigicodeValidator.MAX_TIME_LIMIT_SECONDS.toFloat(),
                            steps = ((DigicodeValidator.MAX_TIME_LIMIT_SECONDS - DigicodeValidator.MIN_TIME_LIMIT_SECONDS) / 30) - 1,
                            enabled = !isLoading
                        )
                    }
                }

                // Show attempts toggle
                SwitchRow(
                    label = stringResource(Res.string.digicode_show_attempts),
                    checked = showAttemptsToPlayer,
                    onCheckedChange = { showAttemptsToPlayer = it },
                    enabled = !isLoading
                )

                // Sound toggle
                SwitchRow(
                    label = stringResource(Res.string.digicode_sound_enabled),
                    checked = soundEnabled,
                    onCheckedChange = { soundEnabled = it },
                    enabled = !isLoading
                )

                if (errorMessage != null) {
                    Text(
                        text = errorMessage,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodySmall
                    )
                }

                if (isLoading) {
                    LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
                }
            }
        },
        confirmButton = {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                TextButton(
                    onClick = onDismiss,
                    enabled = !isLoading
                ) {
                    Text(stringResource(Res.string.cancel))
                }
                Button(
                    onClick = {
                        onConfirm(
                            selectedPlayerId,
                            digitCountInt,
                            maxAttemptsInt,
                            timeLimitSeconds,
                            showAttemptsToPlayer,
                            soundEnabled,
                            solution
                        )
                    },
                    enabled = canSubmit
                ) {
                    Text(stringResource(Res.string.digicode_launch))
                }
            }
        },
        modifier = modifier.clearFocusOnTap()
    )
}
